import * as React from 'react';
import {
  Pagination,
  type PaginationRootProps as ChakraPaginationRootProps,
  type PaginationItemsProps as ChakraPaginationItemsProps,
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, type ButtonProps } from './button';

/**
 * Project Pagination.
 *
 * Thin composition over Chakra v3's built-in `Pagination` — no bespoke
 * recipe, per the design decision to keep this control plain. Prev/Next and
 * page-number chrome come from the project `Button` so a page item reads as
 * the same object as every other button in the app, rather than introducing
 * a one-off style.
 *
 * @example
 * <PaginationRoot count={users.length} pageSize={100} page={page} onPageChange={(d) => setPage(d.page)}>
 *   <HStack gap={1}>
 *     <PaginationPrevTrigger />
 *     <PaginationItems render={(p) => (
 *       <PaginationPageButton key={p.value} page={p.value} currentPage={page} />
 *     )} />
 *     <PaginationNextTrigger />
 *   </HStack>
 * </PaginationRoot>
 */

export type PaginationRootProps = ChakraPaginationRootProps;

export const PaginationRoot = React.forwardRef<
  HTMLDivElement,
  PaginationRootProps
>((props, ref) => <Pagination.Root ref={ref} {...props} />);
PaginationRoot.displayName = 'PaginationRoot';

export const PaginationPrevTrigger = (props: ButtonProps) => (
  <Pagination.PrevTrigger asChild>
    <Button variant="outline" size="xs" aria-label="Previous page" {...props}>
      <ChevronLeft size={14} />
    </Button>
  </Pagination.PrevTrigger>
);

export const PaginationNextTrigger = (props: ButtonProps) => (
  <Pagination.NextTrigger asChild>
    <Button variant="outline" size="xs" aria-label="Next page" {...props}>
      <ChevronRight size={14} />
    </Button>
  </Pagination.NextTrigger>
);

export type PaginationItemsProps = ChakraPaginationItemsProps;

export const PaginationItems = (props: PaginationItemsProps) => (
  <Pagination.Items {...props} />
);

/** One page-number button — `asChild` under `Pagination.Item` so Ark's own click/aria wiring lands on the project `Button`. */
export const PaginationPageButton = ({
  page,
  currentPage,
}: {
  page: number;
  currentPage: number;
}) => (
  <Pagination.Item asChild type="page" value={page}>
    <Button variant={page === currentPage ? 'solid' : 'outline'} size="xs">
      {page}
    </Button>
  </Pagination.Item>
);
